import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetConsignmentLinesQuery, CONSIGNMENT_STATUS, type ConsignmentStatusCode } from '@/entities/consignments';
import { usePermissions } from '@/shared/lib';

import { ConsignmentLineRow, UseConsignmentLinesTableParams } from './types';
import { getConsignmentLineColumns, getConsignmentLineActionColumn } from '../config/columns';

export function useConsignmentLinesTable({
  consignmentId,
  projectId,
  siteId,
  consignmentStatus = CONSIGNMENT_STATUS.NR,
  isReadOnly = false,
  externalData,
  externalIsLoading,
  externalError
}: UseConsignmentLinesTableParams) {
  const { t } = useTranslation();
  const { useConsignmentLinePermissions } = usePermissions();
  const [selectedLineId, setSelectedLineId] = useState<string>('');

  const linePermissions = useConsignmentLinePermissions(isReadOnly, consignmentStatus as ConsignmentStatusCode);
  const canEditLine = linePermissions.canEditLine;
  const canDeleteLine = linePermissions.canDeleteLine;

  // Skip API call if external data is provided
  const {
    data: localData,
    isLoading: localIsLoading,
    error: localError
  } = useGetConsignmentLinesQuery(
    {
      consignmentId,
      projectId,
      siteId
    },
    {
      skip: !consignmentId || !projectId || !siteId || !!externalData
    }
  );

  // Use external data if provided, otherwise use API data
  const data = externalData || localData;

  const columns = useMemo(() => getConsignmentLineColumns(t, data?.totals), [t, data?.totals]);
  const actions = useMemo(
    () => getConsignmentLineActionColumn(t, (line) => setSelectedLineId(line.id)),
    [t, setSelectedLineId]
  );

  const handleCloseModal = useCallback(() => {
    setSelectedLineId('');
  }, []);

  const tableData: ConsignmentLineRow[] = useMemo(() => data?.items.map((item) => ({ ...item })) || [], [data?.items]);

  return {
    // State
    selectedLineId,
    isLoading: externalData ? externalIsLoading : localIsLoading,
    error: externalData ? externalError : localError,

    // Data
    tableData,
    columns,
    actions,

    // Handlers
    handleCloseModal,

    // Permissions
    canEditLine,
    canDeleteLine
  };
}
